import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Button, Container, Badge } from '../ui';
import heroVideo from '../../assets/istatec_cover_final.webm';
import heroVideoSecondary from '../../assets/istatec_cover_secondary.webm';
import fluidSphere from '../../assets/dynamic_fluid_sphere.gif';

export const Hero: React.FC = () => {
  return (
    <>
    <section className="relative overflow-hidden bg-navy-950 pt-20">
      {/* Background Video */}
      <video
        className="absolute inset-x-0 top-20 w-full h-auto"
        src={heroVideo}
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        aria-hidden="true"
      />

      <Container className="relative z-10 pt-[42px] pb-0">
        <div className="max-w-4xl mx-auto text-center">
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Badge variant="accent" className="mb-6 bg-white/10 text-white border border-white/20">
              <img
                src={fluidSphere}
                alt=""
                aria-hidden="true"
                className="w-5 h-5 rounded-full object-cover mr-2"
              />
              AI-Powered Solutions for Modern Enterprises
            </Badge>
          </motion.div>

          {/* Main Heading */}
          <motion.h1
            className="heading-xl text-white mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            Reimagining Businesses with{' '}
            <span className="relative inline-block">
              <span className="relative z-10 bg-gradient-to-r from-accent-300 via-accent-400 to-accent-300 bg-clip-text text-transparent">
                AI-Led
              </span>
              <motion.span
                className="absolute -inset-1 bg-accent-500/20 blur-lg rounded-lg"
                animate={{ opacity: [0.5, 0.8, 0.5] }}
                transition={{ duration: 3, repeat: Infinity }}
              />
            </span>{' '}
            Digital Transformation
          </motion.h1>

          {/* Subheading */}
          <motion.p
            className="text-xl md:text-2xl text-navy-100 mb-8 max-w-2xl mx-auto leading-relaxed drop-shadow"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            Expert tech consulting, software development, and elite technical talent
            &mdash; contract or full-time &mdash; to accelerate what&apos;s next.
          </motion.p>

          {/* Tech Stack Pills */}
          <motion.div
            className="flex flex-wrap justify-center gap-3"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            {[
              'GenAI',
              'Backend',
              'Frontend',
              'Cloud',
              'Data Engineering',
              'Data Science',
              'Salesforce',
              'Cybersecurity',
            ].map((tech, index) => (
              <motion.span
                key={tech}
                className="px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm text-white/90 text-sm font-medium border border-white/10 hover:bg-white/20 hover:border-white/30 transition-all cursor-default"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.5 + index * 0.05 }}
              >
                {tech}
              </motion.span>
            ))}
          </motion.div>
        </div>
      </Container>
    </section>

    {/* Secondary Cover with CTAs */}
    <section className="relative overflow-hidden bg-navy-950">
      <div className="relative w-full aspect-[3240/580] min-h-[180px]">
        <video
          className="absolute inset-0 w-full h-full object-cover"
          src={heroVideoSecondary}
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          aria-hidden="true"
        />

        {/* CTAs overlay */}
        <div className="absolute inset-0 z-10 flex items-center justify-center px-4">
          <motion.div
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Link to="/contact">
              <Button variant="accent" size="lg" rightIcon={<ArrowRight className="w-5 h-5" />}>
                Start Your Project
              </Button>
            </Link>
            <Link to="/services">
              <Button
                variant="ghost"
                size="lg"
                className="text-white hover:bg-white/10 border border-white/20"
              >
                Explore Services
              </Button>
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
    </>
  );
};
